import type { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { Result, Spin } from 'antd';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiErrorEnum } from '../../../enums/ApiErrorEnum';
import { createSharePointMsalInstance } from '../../../lib/msalConfig';
import { SharePointService } from '../../../services/sharePointApiService';
import { getApiErrorCode } from '../../../utils/apiErrorCode';

const sharePointService = new SharePointService();

/**
 * The instance, cached for the tab: the tenant and the app registration belong to the company, so
 * they cannot change while the session lasts, and both the viewer and the editor mount this provider.
 *
 * A FAILED bootstrap is deliberately not cached — an administrator adding the missing app
 * registration must be one reload away, not one new tab away.
 */
let instancePromise: Promise<PublicClientApplication> | null = null;

function loadSharePointMsalInstance(): Promise<PublicClientApplication> {
  instancePromise ??= (async () => {
    const configuration = await sharePointService.getSignInConfig();
    const instance = createSharePointMsalInstance(configuration);

    // Before `initialize()` every call on the instance throws `uninitialized_public_client_application`,
    // and `MsalProvider` does not do it for us.
    await instance.initialize();
    return instance;
  })().catch((failure: unknown) => {
    instancePromise = null;
    throw failure;
  });

  return instancePromise;
}

/** What the subtree can do when there is no usable MSAL instance: nothing, and it must say why. */
type BootstrapError = 'notConfigured' | 'failed';

/**
 * Provides the MSAL instance of the CUSTOMER's tenant to the SharePointGrant viewer and editor.
 *
 * Why a second `MsalProvider` nested under the one in `main.tsx`, rather than making that one
 * asynchronous: the client id and the tenant are read from the company's SharePoint setting
 * (`GET /api/sharepoint/signin/config`), so they are only knowable **after** login and only for the
 * company the user belongs to. The root provider serves the FileGrant EntraID login, which happens
 * before any of that exists — and it must not be made to wait for a call it cannot make.
 *
 * Everything below therefore keeps using `useMsal()` unchanged: React context resolves to the nearest
 * provider, which inside this subtree is this one.
 */
const SharePointMsalProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();

  const [instance, setInstance] = useState<PublicClientApplication | null>(null);
  const [error, setError] = useState<BootstrapError | null>(null);

  useEffect(() => {
    let isCurrent = true;

    loadSharePointMsalInstance()
      .then((loaded) => {
        if (isCurrent) setInstance(loaded);
      })
      .catch((failure: unknown) => {
        if (!isCurrent) return;

        /*
         * Two outcomes only, because only two remedies exist: an administrator has to configure
         * something (nothing the user can do, so say so plainly), or the call itself failed and a
         * retry may work.
         */
        const code = getApiErrorCode(failure);
        const isConfigurationMissing =
          code === ApiErrorEnum.SharePointSpaAppNotConfigured ||
          code === ApiErrorEnum.SharePointNotConfigured ||
          code === ApiErrorEnum.SharePointNotAvailable ||
          code === ApiErrorEnum.SharePointConfigInvalid;

        console.error('SharePoint: MSAL configuration could not be loaded', failure);
        setError(isConfigurationMissing ? 'notConfigured' : 'failed');
      });

    return () => {
      isCurrent = false;
    };
  }, []);

  if (error !== null) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Result
          status={error === 'notConfigured' ? '404' : 500}
          title={t(`sharePoint.signIn.${error}.title`)}
          subTitle={t(`sharePoint.signIn.${error}.description`)}
        />
      </div>
    );
  }

  if (instance === null) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2">
        <Spin size="large" spinning />
        <span className="text-gray-dark text-sm">{t('sharePoint.signIn.loading')}</span>
      </div>
    );
  }

  return <MsalProvider instance={instance}>{children}</MsalProvider>;
};

export default SharePointMsalProvider;
